<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import Button from 'dashboard/components-next/button/Button.vue';
import SlaAPI from 'dashboard/api/fandata/sla';
import RetomadaAPI from 'dashboard/api/fandata/retomada';
import { useAlert } from 'dashboard/composables';
import { useFandataPerfil } from 'dashboard/composables/useFandataPerfil';
import FichaSocio from 'dashboard/components/widgets/conversation/fandata/FichaSocio.vue';

// Painel do Torcedor (M1 one-page): ficha do sócio ao vivo (FENG) + triagem da IA
// (sentimento/assunto/resumo) + histórico de assuntos do contato.
// Categorização = Etiquetas nativas (não conceito à parte).
// O etiquetador saiu em 13/08 (T-97): tabular virou ato de encerrar ou transferir.
const props = defineProps({
  conversationAttributes: { type: Object, default: () => ({}) },
  contact: { type: Object, default: () => ({}) },
});

const { t } = useI18n();
const store = useStore();

// Mantido: o histórico e o modal de tabulação dependem das etiquetas da conta estarem carregadas.
const accountLabels = useMapGetter('labels/getLabels');
const currentChat = useMapGetter('getSelectedChat');
const accountId = useMapGetter('getCurrentAccountId');
const inboxesGetter = useMapGetter('inboxes/getInbox');
const contactConversationsGetter = useMapGetter(
  'contactConversations/getContactConversation'
);

// T-153b: chamar o torcedor de volta por e-mail quando o canal não deixa mais. É contato ATIVO do
// clube — o T-168 separou isso de reabertura —, então é ação de administrador, não de agente.
// O botão não reabre nada: se o torcedor responder, o caminho normal cuida.
const { ehAdmin } = useFandataPerfil();
const enviandoRetomada = ref(false);

const enviarRetomada = async () => {
  enviandoRetomada.value = true;
  try {
    await RetomadaAPI.enviar(currentChat.value.id);
    useAlert(t('FANDATA_SIDEBAR.RETOMADA.ENVIADO'));
  } catch (error) {
    useAlert(
      error?.response?.data?.error || t('FANDATA_SIDEBAR.RETOMADA.ERRO')
    );
  } finally {
    enviandoRetomada.value = false;
  }
};

// Espelha Fandata::Labels: sentimento não é "assunto" e fica fora do histórico.
const SENTIMENTO_TITLES = ['positivo', 'neutro', 'negativo'];
const MESES = [
  'jan',
  'fev',
  'mar',
  'abr',
  'mai',
  'jun',
  'jul',
  'ago',
  'set',
  'out',
  'nov',
  'dez',
];

const TRUTHY = [true, 'true', 'sim', 'yes', 1, '1'];
const FALSY = [false, 'false', 'nao', 'não', 'no', 0, '0'];

const ca = computed(() => props.contact?.custom_attributes || {});
const isTorcedor = computed(() => {
  const v = ca.value.torcedor;
  if (TRUTHY.includes(v)) return true;
  if (FALSY.includes(v)) return false;
  return null;
});

const sentimento = computed(
  () => props.conversationAttributes.fandata_sentimento || ''
);
const assuntoIA = computed(
  () => props.conversationAttributes.fandata_assunto || ''
);
const resumo = computed(
  () => props.conversationAttributes.fandata_resumo || ''
);
const temTriagem = computed(
  () => !!(sentimento.value || assuntoIA.value || resumo.value)
);
const sentimentoStyle = computed(() => {
  const map = {
    negativo: 'bg-n-ruby-3 text-n-ruby-11',
    positivo: 'bg-n-teal-3 text-n-teal-11',
    neutro: 'bg-n-amber-3 text-n-amber-11',
  };
  return map[sentimento.value] || 'bg-n-alpha-2 text-n-slate-11';
});

// === SLA composto (T-04): IA × Atendimento × Pendência(parceiro) + onde estourou ===
const sla = ref(null);
const displayId = computed(() => currentChat.value?.id); // chat.id = display_id (serializer)

const fmtDur = seg => {
  if (seg === null || seg === undefined) return '—';
  if (seg < 60) return '<1min';
  if (seg < 3600) return `${Math.round(seg / 60)}min`;
  const h = Math.floor(seg / 3600);
  const m = Math.round((seg % 3600) / 60);
  return `${h}h${String(m).padStart(2, '0')}`;
};

// T-212: os chamados abertos do mesmo torcedor vêm junto do SLA, mas em estado PRÓPRIO. O SLA é
// descartado quando não está disponível (linha abaixo), e o aviso não pode desaparecer junto:
// conversa sem SLA calculado é exatamente onde o agente mais precisa saber que existe outro
// atendimento correndo.
const chamadosAbertos = ref([]);

const fetchSla = async () => {
  if (!displayId.value) {
    sla.value = null;
    chamadosAbertos.value = [];
    return;
  }
  try {
    const { data } = await SlaAPI.composto(displayId.value);
    sla.value = data?.disponivel ? data : null;
    chamadosAbertos.value = data?.chamados_abertos || [];
  } catch (e) {
    sla.value = null;
    chamadosAbertos.value = [];
  }
};

// Abrir o chamado antigo é o que o agente faz depois de ler o aviso, então ele é clicável.
const abrirChamado = id =>
  window.open(`/app/accounts/${accountId.value}/conversations/${id}`, '_blank');

const textoDoChamado = c => {
  const partes = [c.protocolo];
  if (c.assunto) partes.push(c.assunto);
  if (c.pendencia) partes.push(t('FANDATA_SIDEBAR.CHAMADOS.COM_PARCEIRO', { parceiro: c.pendencia }));
  else if (c.chamado_interno) partes.push(t('FANDATA_SIDEBAR.CHAMADOS.INTERNO'));
  return partes.join(' · ');
};

const slaBlocos = computed(() => {
  const s = sla.value;
  if (!s) return [];
  const blocos = [];
  if (s.ia_seg !== null && s.ia_seg !== undefined) {
    blocos.push({ key: 'ia', label: t('FANDATA_SIDEBAR.SLA.IA'), seg: s.ia_seg });
  }
  blocos.push({
    key: 'humano',
    label: t('FANDATA_SIDEBAR.SLA.HUMANO'),
    seg: s.humano_seg,
  });
  if (s.pausado_seg > 0) {
    blocos.push({
      key: 'pausa',
      label: t('FANDATA_SIDEBAR.SLA.PAUSA'),
      seg: s.pausado_seg,
    });
  }
  return blocos;
});

const slaStatuses = computed(() => {
  const s = sla.value;
  if (!s) return [];
  return [
    {
      key: 'pc',
      label: t('FANDATA_SIDEBAR.SLA.PRIMEIRO_CONTATO'),
      status: s.primeiro_contato,
    },
    {
      key: 'tr',
      label: t('FANDATA_SIDEBAR.SLA.TRATAMENTO'),
      status: s.tratamento,
    },
  ].filter(x => x.status && x.status !== 'na');
});

const slaStatusStyle = status =>
  ({
    dentro: 'bg-n-teal-3 text-n-teal-11',
    cumprido: 'bg-n-teal-3 text-n-teal-11',
    risco: 'bg-n-amber-3 text-n-amber-11',
    estourado: 'bg-n-ruby-3 text-n-ruby-11',
  })[status] || 'bg-n-alpha-2 text-n-slate-11';

const slaStatusLabel = status => t(`FANDATA_SIDEBAR.SLA.STATUS.${status}`);

// T-97 (reunião 13/08): o etiquetador de assunto SAIU daqui. O clube pediu tabulação apenas
// no encerramento e na transferência, porque etiquetar a qualquer momento fazia o rótulo
// acompanhar o palpite do agente no meio do atendimento em vez do desfecho real.
// Quem pede a tabulação agora é o TabulacaoModal, disparado por ResolveAction (encerrar) e
// ConversationAction (transferir). O que sobra aqui é leitura: a sugestão da IA e o histórico.

// === Histórico de assuntos do contato ===
const contactId = computed(() => props.contact?.id);
const toDate = ts =>
  typeof ts === 'number' ? new Date(ts * 1000) : new Date(ts);
const fmtMonth = ts => {
  if (!ts) return '';
  const d = toDate(ts);
  return `${MESES[d.getMonth()]}/${String(d.getFullYear()).slice(-2)}`;
};

// T-115 (devolutiva 27/08): de qual canal ESTA conversa veio.
//
// A informação já existia no topo da tela, mas a Tamara e a Mari pediram no painel, e o motivo
// é operacional: como todo mundo atende todos os canais, o que muda a conduta ao abrir não é
// saber que existe outra conversa, é saber por onde o torcedor está falando AGORA.
const canalAtual = computed(() => {
  const chat = currentChat.value;
  if (!chat) return null;
  const inbox = inboxesGetter.value(chat.inbox_id);
  return {
    nome: inbox?.name || '',
    // O tipo vem do canal nativo e é o que decide o ícone; sem ele, cai no genérico.
    tipo: inbox?.channel_type || chat.meta?.channel || '',
  };
});

const ICONE_POR_CANAL = {
  'Channel::Instagram': 'i-ri-instagram-line',
  'Channel::FacebookPage': 'i-ri-messenger-line',
  'Channel::Whatsapp': 'i-ri-whatsapp-line',
  'Channel::WebWidget': 'i-ri-global-line',
  'Channel::Email': 'i-ri-mail-line',
  'Channel::Api': 'i-ri-terminal-box-line',
};
const iconeCanal = computed(
  () => ICONE_POR_CANAL[canalAtual.value?.tipo] || 'i-ri-chat-3-line'
);


// T-54 (ata 06/08): abertura e última movimentação do ticket, na lateral.
// Dado é nativo (created_at / last_activity_at do Chatwoot); aqui é só exibição.
const fmtDataHora = ts => {
  if (!ts) return '';
  const d = toDate(ts);
  const dia = String(d.getDate()).padStart(2, '0');
  const hora = String(d.getHours()).padStart(2, '0');
  const min = String(d.getMinutes()).padStart(2, '0');
  return `${dia}/${MESES[d.getMonth()]}/${String(d.getFullYear()).slice(-2)} ${hora}:${min}`;
};
const datasTicket = computed(() => {
  const c = currentChat.value;
  if (!c) return null;
  // last_activity_at só existe depois da 1a atividade; cai para a abertura.
  const abertura = c.created_at;
  const movimentacao = c.last_activity_at || c.timestamp || abertura;
  if (!abertura) return null;
  return {
    abertura: fmtDataHora(abertura),
    movimentacao: fmtDataHora(movimentacao),
  };
});
// O ESTADO DO TICKET, PEDIDO PELO CLUBE EM 15/10: "em informações do ticket, precisa ver o
// último status e a tabulação, quando inserida".
//
// A tabulação já estava aqui; o estado não. Quem abre a ficha pelo painel quer saber, antes de
// tudo, se o caso está em aberto, parado esperando ou encerrado, e isso estava só na barra de
// cima da conversa.
const ESTADOS = {
  open: 'ABERTO',
  pending: 'PENDENTE',
  snoozed: 'ADIADO',
  resolved: 'ENCERRADO',
};

const estadoDoTicket = computed(() => {
  const chave = currentChat.value?.status;
  if (!chave) return null;

  return {
    rotulo: t(`FANDATA_SIDEBAR.ESTADO.${ESTADOS[chave] || 'ABERTO'}`),
    // Encerrado é o único que muda de cor: é o estado em que não há mais o que fazer, e quem
    // olha a ficha precisa ver isso sem ler.
    classe:
      chave === 'resolved'
        ? 'bg-n-slate-3 text-n-slate-11'
        : 'bg-n-amber-3 text-n-amber-11',
  };
});

const historico = computed(() => {
  const all = contactConversationsGetter.value(contactId.value) || [];
  return all
    .filter(c => c.id !== currentChat.value?.id)
    .map(c => ({
      id: c.id,
      assuntos: (c.labels || []).filter(l => !SENTIMENTO_TITLES.includes(l)),
      ts: c.created_at || c.last_activity_at || c.timestamp,
    }))
    .filter(c => c.assuntos.length)
    .sort((a, b) => toDate(b.ts) - toDate(a.ts))
    .slice(0, 5);
});

// === Chamado interno (Cenario B) ===
const origemConversationId = computed(() => currentChat.value?.id);

const loadContactConversations = () => {
  if (contactId.value) {
    store.dispatch('contactConversations/get', contactId.value);
  }
};
onMounted(() => {
  if (!accountLabels.value?.length) store.dispatch('labels/get');
  loadContactConversations();
  fetchSla();
});
watch(contactId, loadContactConversations);
// Recarrega o SLA composto ao trocar de conversa, ao marcar/retomar pendência ou ao mudar o status.
watch(
  () =>
    `${displayId.value}:${currentChat.value?.status}:${!!props.conversationAttributes?.fandata_pendencia}`,
  fetchSla
);
</script>

<template>
  <div class="flex flex-col gap-3 px-1 py-1">
    <!-- Ficha do sócio ao vivo (R1.21), em PRIMEIRO lugar: é a resposta que o agente busca
         antes de qualquer outra, e o dado vem da FENG no momento da consulta, não do cadastro.
         Sem a borda superior dos demais blocos, porque abre o painel. -->
    <FichaSocio
      class="!pt-0 !border-t-0"
      :conversation-id="currentChat && currentChat.id"
      :cpf="String(ca.cpf || '')"
    />

    <!-- T-212 — o torcedor já tem atendimento aberto.
         Fica no ALTO, logo abaixo da ficha de sócio, porque é informação que muda o que o
         agente vai dizer na primeira frase. Embaixo, junto do histórico, ele leria depois de
         já ter respondido. Quase sempre não aparece: a maioria volta sem caso em aberto. -->
    <div
      v-if="chamadosAbertos.length"
      class="flex flex-col gap-1.5 rounded-lg border border-n-amber-6 bg-n-amber-2 px-3 py-2"
    >
      <span class="text-xs font-semibold text-n-amber-11">
        {{
          chamadosAbertos.length === 1
            ? t('FANDATA_SIDEBAR.CHAMADOS.TITULO_UM')
            : t('FANDATA_SIDEBAR.CHAMADOS.TITULO', { n: chamadosAbertos.length })
        }}
      </span>
      <button
        v-for="c in chamadosAbertos"
        :key="c.id"
        type="button"
        class="text-left text-[12.5px] text-n-slate-12 underline decoration-dotted"
        @click="abrirChamado(c.id)"
      >
        {{ textoDoChamado(c) }}
        <span v-if="c.mesmo_tema" class="font-semibold text-n-amber-11">
          {{ t('FANDATA_SIDEBAR.CHAMADOS.MESMO_TEMA') }}
        </span>
      </button>
    </div>

    <!-- Ficha do torcedor. O selo de sócio/não sócio e o do plano saíram daqui em 20/08: a
         ficha logo acima diz a mesma coisa com mais informação e com dado do momento, enquanto
         estes liam o espelho gravado no cadastro. Dois avisos sobre o mesmo fato, um podendo
         contradizer o outro, é pior do que um só. -->
    <div
      v-if="isTorcedor === false"
      class="flex flex-wrap items-center gap-2"
    >
      <span
        class="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium bg-n-alpha-1 text-n-slate-10"
      >
        {{ t('FANDATA_SIDEBAR.NAO_TORCEDOR') }}
      </span>
    </div>

    <!-- T-115: canal desta conversa, no painel e não só no topo da tela -->
    <div
      v-if="canalAtual && canalAtual.nome"
      class="flex items-center gap-1.5 text-xs"
    >
      <span :class="iconeCanal" class="size-4 text-n-slate-11" />
      <span class="text-n-slate-10">{{ t('FANDATA_SIDEBAR.CANAL') }}</span>
      <span class="font-medium text-n-slate-12">{{ canalAtual.nome }}</span>
    </div>

    <!-- T-228 (24/09): o @ do Instagram saiu daqui. O T-131 o trouxe para este painel porque era
         o único lugar onde aparecia; agora ele mora na área do CONTATO, lá em cima, escrito por
         extenso. Este bloco é do TICKET, e perfil é da pessoa. -->

    <!-- Datas do ticket (T-54): abertura + última movimentação. O estado entra junto desde
         15/10, por pedido do clube: quem abre a ficha quer saber primeiro em que pé está. -->
    <div
      v-if="datasTicket"
      class="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-n-slate-11"
    >
      <span
        v-if="estadoDoTicket"
        class="px-1.5 py-0.5 rounded font-medium tracking-wide"
        :class="estadoDoTicket.classe"
      >
        {{ estadoDoTicket.rotulo }}
      </span>
      <span class="inline-flex items-center gap-1">
        <span class="text-n-slate-10">{{ t('FANDATA_SIDEBAR.DATAS.ABERTURA') }}</span>
        <span class="font-medium text-n-slate-12">{{ datasTicket.abertura }}</span>
      </span>
      <span class="inline-flex items-center gap-1">
        <span class="text-n-slate-10">
          {{ t('FANDATA_SIDEBAR.DATAS.MOVIMENTACAO') }}
        </span>
        <span class="font-medium text-n-slate-12">
          {{ datasTicket.movimentacao }}
        </span>
      </span>
    </div>

    <!-- Triagem da IA -->
    <div v-if="temTriagem" class="flex flex-col gap-2">
      <div class="flex flex-wrap items-center gap-2">
        <span
          v-if="sentimento"
          class="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-semibold capitalize"
          :class="sentimentoStyle"
        >
          {{ sentimento }}
        </span>
      </div>
      <p v-if="resumo" class="text-sm text-n-slate-11 leading-snug">
        {{ resumo }}
      </p>
    </div>
    <p v-else class="text-xs text-n-slate-10">
      {{ t('FANDATA_SIDEBAR.SEM_TRIAGEM') }}
    </p>

    <!-- SLA composto (T-04): decomposição do tempo + onde estourou -->
    <div
      v-if="sla"
      class="flex flex-col gap-1.5 pt-1 border-t border-n-weak"
    >
      <span class="text-xs font-medium text-n-slate-11">
        {{ t('FANDATA_SIDEBAR.SLA.TITULO') }}
      </span>
      <div class="flex flex-wrap gap-1.5">
        <span
          v-for="b in slaBlocos"
          :key="b.key"
          class="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium bg-n-alpha-2"
        >
          <span class="text-n-slate-10">{{ b.label }}</span>
          <span class="font-semibold text-n-slate-12">{{ fmtDur(b.seg) }}</span>
        </span>
      </div>
      <div
        v-if="sla.pendencias && sla.pendencias.length"
        class="flex flex-wrap items-center gap-1 text-xs text-n-slate-10"
      >
        <span>{{ t('FANDATA_SIDEBAR.SLA.POR_PARCEIRO') }}:</span>
        <span
          v-for="p in sla.pendencias"
          :key="p.parceiro_slug"
          class="px-1.5 py-0.5 rounded bg-n-alpha-1 text-n-slate-11 capitalize"
        >
          {{ p.parceiro_nome }} · {{ fmtDur(p.seg) }}
        </span>
      </div>
      <div v-if="slaStatuses.length" class="flex flex-wrap gap-1.5">
        <span
          v-for="st in slaStatuses"
          :key="st.key"
          class="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium"
          :class="slaStatusStyle(st.status)"
        >
          {{ st.label }}: {{ slaStatusLabel(st.status) }}
        </span>
      </div>

      <!-- T-122: o prazo está apertado E o caso está com um terceiro. Dizer só "em risco"
           deixa o agente sem ação; dizer com quem está e há quanto tempo dá a ação: cobrar
           o parceiro antes de estourar, em vez de descobrir depois no relatório. -->
      <div
        v-if="sla.risco_por_parceiro && sla.aguardando_parceiro"
        class="flex items-start gap-2 px-2 py-1.5 mt-0.5 text-xs rounded-md bg-n-amber-3 text-n-amber-11"
      >
        <span class="flex-shrink-0 mt-px i-lucide-alarm-clock size-4" />
        <span>
          {{
            t('FANDATA_SIDEBAR.SLA.RISCO_PARCEIRO', {
              parceiro: sla.aguardando_parceiro.parceiro,
              tempo: fmtDur(sla.aguardando_parceiro.segundos),
            })
          }}
        </span>
      </div>
    </div>

    <!-- Histórico de assuntos do contato -->
    <div
      v-if="historico.length"
      class="flex flex-col gap-1.5 pt-1 border-t border-n-weak"
    >
      <span class="text-xs font-medium text-n-slate-11">
        {{ t('FANDATA_SIDEBAR.HISTORICO') }}
      </span>
      <div class="flex flex-col gap-1">
        <div
          v-for="h in historico"
          :key="h.id"
          class="flex flex-wrap items-center gap-1 text-xs"
        >
          <span
            v-for="a in h.assuntos"
            :key="a"
            class="px-1.5 py-0.5 rounded bg-n-alpha-1 text-n-slate-11 capitalize"
          >
            {{ a }}
          </span>
          <span
            class="text-n-slate-10 before:content-['·'] before:mr-1 before:text-n-slate-9"
          >
            {{ fmtMonth(h.ts) }}
          </span>
        </div>
      </div>
    </div>

    <!-- T-153b: convite de retomada, só para administrador. Fica ao lado do SLA porque é onde se
         percebe que a conversa parou: o prazo estourado é o gatilho para chamar de volta. -->
    <div v-if="ehAdmin" class="flex flex-col gap-1 pt-3 mt-3 border-t border-n-weak">
      <Button
        :label="t('FANDATA_SIDEBAR.RETOMADA.BOTAO')"
        icon="i-lucide-mail"
        variant="faded"
        color="slate"
        size="sm"
        :is-loading="enviandoRetomada"
        @click="enviarRetomada"
      />
      <span class="text-xs text-n-slate-11">
        {{ t('FANDATA_SIDEBAR.RETOMADA.HINT') }}
      </span>
    </div>

    <!-- T-155e: "Abrir chamado interno" saiu daqui. A Tamara em 03/09: "o chamado interno nada
         mais é do que marcar pendência, então ele só tem que ter um caminho para fazer isso".
         O mecanismo continua existindo por trás; o que muda é a porta de entrada, que passa a
         ser a pendência (menu Resolver). -->

  </div>
</template>
